// src/pages/admin/Dashboard.jsx
import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useBranding } from "../../hooks/useBranding";
import { logActivity, ROLE_LABELS, ROLE_COLORS } from "../../firebase/auth";
import {
  subscribeDesigns, fetchDesigns, subscribeBanners, subscribeCategories,
  deleteDesign, toggleDesignActive,
  deleteBanner,
  deleteCategory,
  saveBranding, uploadLogoFile, toggleCategoryActive,
  subscribeCustomers, addCustomer, updateCustomer, deleteCustomer,
  subscribeTransports, addTransport, updateTransport, deleteTransport,
  subscribeNotificationTemplates,
  addNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate,
} from "../../firebase/services";
import { B, fonts, Btn, Toast, Spinner, useConfirmModal } from "../../components/UI";
import useQuotations from "../../hooks/useQuotations";
import DesignForm from "./DesignForm";
import AdminManagement from "./AdminManagement";
import ActivityLog from "./ActivityLog";
import DesignsTab from "./tabs/DesignsTab";
import CustomersTab from "./tabs/CustomersTab";
import TransportsTab from "./tabs/TransportsTab";
import CreateQuotationTab from "./tabs/CreateQuotationTab";
import AllQuotationsTab from "./tabs/AllQuotationsTab";
import CategoriesTab from "./tabs/CategoriesTab";
import BannersTab from "./tabs/BannersTab";
import BrandingTab from "./tabs/BrandingTab";
import SettingsTab from "./tabs/SettingsTab";
import NotificationsTab from "./tabs/NotificationsTab";
import AppointmentsSettingsTab from "./tabs/AppointmentsSettingsTab";
import BookingsTab from "./tabs/BookingsTab";
import DesignRow from "./tabs/DesignRow";
import CategoryForm from "./forms/CategoryForm";
import BannerForm from "./forms/BannerForm";
import CustomerForm from "./forms/CustomerForm";
import TransportForm from "./forms/TransportForm";
import QuotationBuilder from "./forms/QuotationBuilder";
import { printQuotationDocument } from "./print/quotationPrint";
import { generateQuotationPdf, blobToBase64 } from "../../utils/pdf";
import {
  normalizeTransportName, normalizeTransportGst,
  findTransportConflict, getTransportConflictMessage,
} from "../../utils/transport";

// ─── Tab config based on role ─────────────────────────────────────────────────
const getTabs = (permissions, isSuperAdmin) => {
  const tabs = [];
  tabs.push("Designs");
  if (isSuperAdmin) tabs.push("Customers");
  if (isSuperAdmin) tabs.push("Transports");
  if (permissions?.canManageCustomers) tabs.push("Create Quotation");
  if (isSuperAdmin) tabs.push("All Quotations");
  if (permissions?.canManageCategories) tabs.push("Categories");
  if (permissions?.canManageBanners) tabs.push("Banners");
  if (permissions?.canManageBranding) tabs.push("Branding");
  if (isSuperAdmin) {
    tabs.push("Team");
    tabs.push("Activity");
    tabs.push("Notifications");
    tabs.push("Appointments");
    tabs.push("Bookings");
  }
  if (permissions?.canManageBranding) tabs.push("Settings");
  return tabs;
};

const SidebarNav = ({ tabs, active, onChange, onBack, canGoBack }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <button
      type="button"
      onClick={onBack}
      disabled={!canGoBack}
      aria-label="Go back to previous dashboard state"
      title={canGoBack ? "Go back to previous state" : "No previous state"}
      style={{
        background: canGoBack ? B.text : B.cream,
        border: `1px solid ${canGoBack ? B.text : B.borderLight}`,
        borderRadius: 10,
        padding: "11px 14px",
        fontWeight: 700,
        color: canGoBack ? B.white : B.textLight,
        cursor: canGoBack ? "pointer" : "not-allowed",
        fontSize: 13,
        transition: "all 0.2s",
        fontFamily: fonts.body,
        textAlign: "left",
        opacity: canGoBack ? 1 : 0.7,
      }}
    >
      ← Back
    </button>
    <div style={{ height: 1, background: B.borderLight, margin: "2px 0 4px" }} />
    {tabs.map(t => (
      <button key={t} onClick={() => onChange(t)}
        style={{
          background: active === t ? `${B.orange}14` : "transparent",
          border: `1px solid ${active === t ? `${B.orange}44` : B.borderLight}`,
          borderRadius: 10,
          padding: "11px 14px",
          fontWeight: active === t ? 700 : 600,
          color: active === t ? B.orange : B.textMid,
          cursor: "pointer",
          fontSize: 13,
          whiteSpace: "nowrap",
          transition: "all 0.2s",
          fontFamily: fonts.body,
          textAlign: "left",
        }}>
        {t}
      </button>
    ))}
  </div>
);

const Stat = ({ label, value, color }) => (
  <div style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 12, padding: "18px 20px", borderLeft: `4px solid ${color}` }}>
    <div style={{ fontSize: 30, fontWeight: 800, color, fontFamily: fonts.adminDisplay }}>{value}</div>
    <div style={{ fontSize: 12, color: B.textMid, marginTop: 3, fontFamily: fonts.body }}>{label}</div>
  </div>
);


// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user, admin, profile, permissions, isSuperAdmin, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const tabs = getTabs(permissions, isSuperAdmin);
  const defaultTab = isSuperAdmin ? "Create Quotation" : "Designs";
  const [tab, setTab] = useState(defaultTab);
  const [tabHistory, setTabHistory] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [notificationTemplates, setNotificationTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editDes, setEditDes] = useState(null);
  const [showDesFm, setShowDesFm] = useState(false);
  const [editBan, setEditBan] = useState(null);
  const [showBanFm, setShowBanFm] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [showCatFm, setShowCatFm] = useState(false);
  const [brandForm, setBrandForm] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [brandSaving, setBrandSaving] = useState(false);
  const [desSearch, setDesSearch] = useState("");
  const [desCategory, setDesCategory] = useState("all");
  const [designPage, setDesignPage] = useState(1);
  const [pendingCrudOps, setPendingCrudOps] = useState(0);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerPage, setCustomerPage] = useState(1);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [viewCustomer, setViewCustomer] = useState(null);
  const [transports, setTransports] = useState([]);
  const [transportSearch, setTransportSearch] = useState("");
  const [transportPage, setTransportPage] = useState(1);
  const [showTransportForm, setShowTransportForm] = useState(false);
  const [transportSaving, setTransportSaving] = useState(false);
  const [editTransport, setEditTransport] = useState(null);
  const [viewTransport, setViewTransport] = useState(null);
  const { confirm, confirmModal } = useConfirmModal();
  const [isCompactLayout, setIsCompactLayout] = useState(() => window.innerWidth <= 1280);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 1280);

  // Always-on live listeners for the lighter collections shared across tabs
  // (banners, categories, customers, notification templates). Designs is handled
  // separately below because it is the heaviest collection (~500 documents);
  // always subscribing to it regardless of tab was a major source of the read
  // + listener spikes reported on the quotation tab.
  useEffect(() => {
    if (!admin) { navigate("/admin-login"); return; }
    const u2 = subscribeBanners(setBanners);
    const u3 = subscribeCategories(setCategories);
    const u4 = subscribeCustomers(setCustomers);
    const u5 = subscribeTransports(setTransports);
    const u7 = subscribeNotificationTemplates(setNotificationTemplates);
    return () => { u2(); u3(); u4(); u5(); u7(); };
  }, [admin, navigate]);

  // Designs: keep a live snapshot ONLY while the admin is on the Designs tab
  // (where designs are actively created/edited). On every other tab (e.g. the
  // Create Quotation design dropdown) fetch the catalogue once with a getDocs
  // and cache it in state, so we never hold an always-on, unbounded listener
  // over all ~500 design documents re-reading them on each write / hot reload.
  const designsLoadedRef = useRef(false);
  // Refined after useQuotations() below populates quotationSettings. Declared here
  // because the design-loading effect above must read it before that hook runs.
  let fetchDesignDetails = false;
  useEffect(() => {
    if (!admin) return;
    if (tab === "Designs") {
      const u1 = subscribeDesigns(d => { setDesigns(d); setLoading(false); });
      return () => u1();
    }
    // Non-Designs tabs: only load the heavy (~500 doc) design catalogue when the
    // "Auto-fetch design info in quotation" setting is enabled. When disabled we
    // skip the read entirely, so quotation creation never pulls the design list.
    if (designsLoadedRef.current || !fetchDesignDetails) {
      setLoading(false);
      return;
    }
    setLoading(true);
    let active = true;
    (async () => {
      let list;
      try { list = await fetchDesigns(); }
      catch (e) { if (active) setLoading(false); return; }
      if (active) {
        designsLoadedRef.current = true;
        setDesigns(list);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [admin, tab, fetchDesignDetails]);

  useEffect(() => { if (!brandForm) setBrandForm({ ...branding }); }, [branding]);

  useEffect(() => {
    const updateLayout = () => {
      const compact = window.innerWidth <= 1280;
      setIsCompactLayout(compact);
      if (!compact) setSidebarOpen(true);
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  const resetForms = () => {
    setShowDesFm(false); setEditDes(null);
    setShowBanFm(false); setEditBan(null);
    setShowCatFm(false); setEditCat(null);
    setShowCustomerForm(false); setEditCustomer(null); setViewCustomer(null);
    setViewQuotation(null);
  };

  const showToast = (msg, color = B.orange) => { setToast({ msg, color }); setTimeout(() => setToast(null), 3200); };
  const setCrudBusy = isBusy => setPendingCrudOps(prev => isBusy ? prev + 1 : Math.max(0, prev - 1));
  const isCrudBusy = pendingCrudOps > 0;
  const headerTitle = tab || "Dashboard";
  const canGoBack = tabHistory.length > 0;

  // Quotation + bill state, filtering, and all actions (shared with mobile admin dashboard)
  const {
    quotations, quotationNumbers, quotationSettings, quotationSaving, settingsSaving, whatsappSending,
    viewQuotation, setViewQuotation, editQuotation, setEditQuotation,
    quotationPrintPrompt, handlePrintPromptClose,
    quotationSearch, setQuotationSearch,
    quotationCustomerFilter, setQuotationCustomerFilter,
    quotationTypeFilter, setQuotationTypeFilter,
    quotationDateFrom, setQuotationDateFrom,
    quotationDateTo, setQuotationDateTo,
    quotationPage, setQuotationPage,
    filteredQuotations, paginatedQuotations, quotationTotalPages, quotationPageSize,
    handleSaveQuotation, handleSaveQuotationSettings, handleDeleteQuotation,
    handlePrintQuotation, handlePrintTransportSlip, handleSendWhatsApp, handleSendWhatsAppFromList,
    handleSendUpiQrFromList,
    formatDateTime, formatCurrency,
  } = useQuotations({
    user, profile, permissions, branding, designs, customers,
    showToast, setCrudBusy, confirm,
  });

  // When the "Auto-fetch design info in quotation" setting is off we skip loading
  // the design catalogue on the quotation tab (see the designsLoading effect above).
  fetchDesignDetails = Boolean(quotationSettings?.fetchDesignDetails);

  const goToTab = nextTab => {
    if (nextTab === tab) return;
    setTabHistory(prev => [...prev, tab]);
    setTab(nextTab);
    if (isCompactLayout) setSidebarOpen(false);
    resetForms();
  };

  const goBackToPreviousState = () => {
    if (!canGoBack) return;
    const previousTab = tabHistory[tabHistory.length - 1];
    setTabHistory(prev => prev.slice(0, -1));
    setTab(previousTab);
    if (isCompactLayout) setSidebarOpen(false);
    resetForms();
  };

  // Redirect from the All Quotations tab: figure out which customer a quotation
  // belongs to and open that customer's details page (via the Customers tab).
  const handleOpenCustomerFromQuotation = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    if (tab !== "Customers") goToTab("Customers"); // goToTab's resetForms() clears viewCustomer
    setShowCustomerForm(false);
    setEditCustomer(null);
    setViewCustomer(customer); // set last so it wins the batched state update
  };

  const activeDesigns = designs.filter(d => d.active);
  const searchTerm = desSearch.trim().toLowerCase();
  const filteredDesigns = designs.filter(d => {
    const byCategory = desCategory === "all" ? true : d.categoryId === desCategory;
    if (!byCategory) return false;
    if (!searchTerm) return true;
    const categoryName = categories.find(c => c.id === d.categoryId)?.name || "";
    return [d.designNo, d.design_number, d.price, d.colors, d.fabric, d.setType, d.setPieces, d.work, d.sizes, categoryName]
      .some(f => String(f || "").toLowerCase().includes(searchTerm));
  });
  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(filteredDesigns.length / pageSize));
  const paginatedDesigns = filteredDesigns.slice((designPage - 1) * pageSize, designPage * pageSize);

  useEffect(() => { setDesignPage(1); }, [desSearch, desCategory]);

  const nextBannerOrder = () => banners.length ? Math.max(...banners.map(b => b.order || 0)) + 1 : 1;
  const nextCategoryOrder = () => categories.length ? Math.max(...categories.map(c => c.order || 0)) + 1 : 1;
  const customerSearchTerm = customerSearch.trim().toLowerCase();
  const filteredCustomers = customers.filter(c => {
    if (c.status === 'deleted') return false;
    if (!customerSearchTerm) return true;
    return [c.customer_name, c.address, c.transport_name, c.city, c.pincode, c.mobile_number, c.firm_name, c.agent_name, c.gst_number, c.status]
      .some(field => String(field || "").toLowerCase().includes(customerSearchTerm));
  });
  const customerPageSize = 10;
  const customerTotalPages = Math.max(1, Math.ceil(filteredCustomers.length / customerPageSize));
  const paginatedCustomers = filteredCustomers.slice((customerPage - 1) * customerPageSize, customerPage * customerPageSize);

  useEffect(() => { setCustomerPage(1); }, [customerSearch]);

  const transportSearchTerm = transportSearch.trim().toLowerCase();
  const filteredTransports = transports.filter(t => {
    if (t.status === 'deleted') return false;
    if (!transportSearchTerm) return true;
    return [t.name, t.gst, t.status]
      .some(field => String(field || "").toLowerCase().includes(transportSearchTerm));
  });
  const transportPageSize = 10;
  const transportTotalPages = Math.max(1, Math.ceil(filteredTransports.length / transportPageSize));
  const paginatedTransports = filteredTransports.slice((transportPage - 1) * transportPageSize, transportPage * transportPageSize);

  useEffect(() => { setTransportPage(1); }, [transportSearch]);

  const handleToggleDesign = async d => {
    setCrudBusy(true);
    try {
      await toggleDesignActive(d.id, !d.active, d.designNo);
      await logActivity(profile.uid, d.active ? "design_oos" : "design_restore", "Design status changed");
      showToast(d.active ? "Marked out of stock" : "Restored", d.active ? B.red : B.green);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCrudBusy(false);
    }
  };

  const handleDeleteDesign = async d => {
    const confirmed = await confirm({
      title: "Delete design?",
      message: "This design will be permanently deleted from the admin catalog.",
      confirmLabel: "Delete Design",
    });
    if (!confirmed) return;
    setCrudBusy(true);
    try {
      await deleteDesign(d.id, d.images || (d.imageUrl ? [d.imageUrl] : []), d.designNo);
      await logActivity(profile.uid, "design_delete", "Design deleted");
      showToast("Design deleted", B.red);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCrudBusy(false);
    }
  };

  const handleDeleteBanner = async b => {
    const confirmed = await confirm({
      title: "Delete banner?",
      message: "This banner will be removed from the website carousel.",
      confirmLabel: "Delete Banner",
    });
    if (!confirmed) return;
    setCrudBusy(true);
    try {
      await deleteBanner(b.id, b.imageUrl);
      showToast("Banner deleted", B.red);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCrudBusy(false);
    }
  };

  const handleDeleteCat = async c => {
    const confirmed = await confirm({
      title: "Delete category?",
      message: `Category "${c.name}" will be deleted from admin and catalog filters.`,
      confirmLabel: "Delete Category",
    });
    if (!confirmed) return;
    setCrudBusy(true);
    try {
      await deleteCategory(c.id);
      showToast("Category deleted", B.red);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCrudBusy(false);
    }
  };

  const handleToggleCat = async (c) => {
    await toggleCategoryActive(c.id, !c.active);
    showToast(
      c.active ? `"${c.name}" hidden from catalog` : `"${c.name}" visible in catalog`,
      c.active ? B.red : B.green
    );
  };

  const handleBrandSave = async () => {
    setBrandSaving(true);
    setCrudBusy(true);
    try {
      let updates = { ...brandForm };
      if (logoFile) updates.logoUrl = await uploadLogoFile(logoFile);
      await saveBranding(updates);
      await logActivity(profile.uid, "branding_update", "Updated branding settings");
      showToast("Branding updated ✓");
    } catch (e) { showToast(e.message, B.red); }
    finally {
      setBrandSaving(false);
      setCrudBusy(false);
    }
  };

  const handleSaveCustomer = async (payload) => {
    if (!payload.customer_name?.trim()) {
      showToast("Customer name is required", B.red);
      return;
    }
    setCustomerSaving(true);
    setCrudBusy(true);
    try {
      if (editCustomer) {
        await updateCustomer(editCustomer.id, payload);
        showToast("Customer updated");
      } else {
        await addCustomer(payload);
        showToast("Customer added");
      }
      setShowCustomerForm(false);
      setEditCustomer(null);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCustomerSaving(false);
      setCrudBusy(false);
    }
  };

  const handleDeleteCustomer = async (customer) => {
    const confirmed = await confirm({
      title: "Delete customer?",
      message: `Customer "${customer.customer_name}" will be marked as deleted.`,
      confirmLabel: "Delete Customer",
    });
    if (!confirmed) return;
    setCrudBusy(true);
    try {
      await deleteCustomer(customer.id);
      showToast("Customer marked as deleted", B.red);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCrudBusy(false);
    }
  };

  const handleSaveTransport = async (payload) => {
    // Code-level validation: normalize the name (Title Case) and GST (UPPER
    // CASE), then enforce a unique (name + GST) combination — or, when the GST
    // is empty, a unique name — against the already-subscribed transports list.
    const name = normalizeTransportName(payload.name);
    const gst = normalizeTransportGst(payload.gst);
    if (!name) {
      showToast("Transport name is required", B.red);
      return;
    }
    const conflict = findTransportConflict(transports, { name, gst }, editTransport?.id);
    if (conflict) {
      showToast(getTransportConflictMessage(conflict, gst), B.red);
      return;
    }
    const normalizedPayload = { ...payload, name, gst };
    setTransportSaving(true);
    setCrudBusy(true);
    try {
      if (editTransport) {
        await updateTransport(editTransport.id, normalizedPayload);
        showToast("Transport updated");
      } else {
        await addTransport(normalizedPayload);
        showToast("Transport added");
      }
      setEditTransport(null);
      setViewTransport(null);
      setShowTransportForm(false);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setTransportSaving(false);
      setCrudBusy(false);
    }
  };

  const handleDeleteTransport = async (transport) => {
    const confirmed = await confirm({
      title: "Delete transport?",
      message: `Transport "${transport.name}" will be marked as deleted.`,
      confirmLabel: "Delete Transport",
    });
    if (!confirmed) return;
    setCrudBusy(true);
    try {
      await deleteTransport(transport.id);
      showToast("Transport marked as deleted", B.red);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCrudBusy(false);
    }
  };


  if (!admin) return null;


  return (
    <>
      <Helmet>
        <title>Dashboard — {branding.brandName}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Toast toast={toast} />
      {confirmModal}

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "32px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}>
            {isCompactLayout && (
              <button
                type="button"
                onClick={() => setSidebarOpen(prev => !prev)}
                aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
                style={{
                  border: `1px solid ${B.borderLight}`,
                  background: B.white,
                  color: B.text,
                  borderRadius: 10,
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontFamily: fonts.body,
                }}
              >
                {sidebarOpen ? "✕ Hide Menu" : "☰ Show Menu"}
              </button>
            )}
            <h1 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 26, color: B.text, fontFamily: fonts.adminDisplay }}>{headerTitle}</h1>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexDirection: "column" }}>
            <p style={{ margin: 0, fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>
              {profile?.name} · {branding.brandName}
            </p>
            <span style={{ fontSize: 11, background: `${ROLE_COLORS[profile?.role]}18`, color: ROLE_COLORS[profile?.role], border: `1px solid ${ROLE_COLORS[profile?.role]}44`, padding: "1px 8px", borderRadius: 4, fontWeight: 700, fontFamily: fonts.body }}>
              {ROLE_LABELS[profile?.role]}
            </span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: (!isCompactLayout || sidebarOpen) ? "240px minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 24, alignItems: "start" }}>
          {sidebarOpen && (
          <aside style={{ position: "sticky", top: 20 }}>
            <div style={{ border: `1px solid ${B.borderLight}`, borderRadius: 14, background: B.white, padding: 12 }}>
              <SidebarNav tabs={tabs} active={tab} onChange={goToTab} onBack={goBackToPreviousState} canGoBack={canGoBack} />
            </div>
          </aside>
          )}

          <section>
            {/* ── DESIGNS ── */}
            {tab === "Designs" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
                  <Stat label="Total Designs" value={designs.length} color={B.orange} />
                  <Stat label="Active" value={activeDesigns.length} color={B.green} />
                  <Stat label="Out of Stock" value={designs.length - activeDesigns.length} color={B.red} />
                  <Stat label="Categories" value={categories.length} color="#2196F3" />
                </div>
                <DesignsTab
                  designs={designs}
                  activeDesigns={activeDesigns}
                  categories={categories}
                  permissions={permissions}
                  showDesFm={showDesFm}
                  editDes={editDes}
                  loading={loading}
                  filteredDesigns={filteredDesigns}
                  paginatedDesigns={paginatedDesigns}
                  desSearch={desSearch}
                  desCategory={desCategory}
                  setDesSearch={setDesSearch}
                  setDesCategory={setDesCategory}
                  setShowDesFm={setShowDesFm}
                  setEditDes={setEditDes}
                  setCrudBusy={setCrudBusy}
                  handleToggleDesign={handleToggleDesign}
                  handleDeleteDesign={handleDeleteDesign}
                  pageSize={pageSize}
                  designPage={designPage}
                  totalPages={totalPages}
                  setDesignPage={setDesignPage}
                  DesignForm={DesignForm}
                  DesignRow={DesignRow}
                  showToast={showToast}
                />
              </>
            )}

            {/* ── CUSTOMERS ── */}
            {tab === "Customers" && (
              <CustomersTab
                customers={customers}
                showFirmName={Boolean(quotationSettings?.showFirmName)}
                filteredCustomers={filteredCustomers}
                paginatedCustomers={paginatedCustomers}
                permissions={permissions}
                customerSearch={customerSearch}
                setCustomerSearch={setCustomerSearch}
                customerPage={customerPage}
                customerPageSize={customerPageSize}
                customerTotalPages={customerTotalPages}
                setCustomerPage={setCustomerPage}
                showCustomerForm={showCustomerForm}
                setShowCustomerForm={setShowCustomerForm}
                editCustomer={editCustomer}
                setEditCustomer={setEditCustomer}
                viewCustomer={viewCustomer}
                setViewCustomer={setViewCustomer}
                handleSaveCustomer={handleSaveCustomer}
                customerSaving={customerSaving}
                handleDeleteCustomer={handleDeleteCustomer}
                CustomerForm={CustomerForm}
                transports={transports}
                designs={designs}
                showToast={showToast}
              />
            )}

            {/* ── TRANSPORTS ── */}
            {tab === "Transports" && (
              <TransportsTab
                transports={transports}
                filteredTransports={filteredTransports}
                paginatedTransports={paginatedTransports}
                permissions={permissions}
                transportSearch={transportSearch}
                setTransportSearch={setTransportSearch}
                transportPage={transportPage}
                transportPageSize={transportPageSize}
                transportTotalPages={transportTotalPages}
                setTransportPage={setTransportPage}
                showTransportForm={showTransportForm}
                setShowTransportForm={setShowTransportForm}
                editTransport={editTransport}
                setEditTransport={setEditTransport}
                viewTransport={viewTransport}
                setViewTransport={setViewTransport}
                handleSaveTransport={handleSaveTransport}
                transportSaving={transportSaving}
                handleDeleteTransport={handleDeleteTransport}
                TransportForm={TransportForm}
                showToast={showToast}
              />
            )}

            {/* ── CREATE QUOTATION ── */}
            {tab === "Create Quotation" && (
              <CreateQuotationTab
                customers={customers}
                transports={transports}
                designs={designs}
                handleSaveQuotation={handleSaveQuotation}
                quotationSaving={quotationSaving}
                editQuotation={editQuotation}
                setEditQuotation={setEditQuotation}
                QuotationBuilder={QuotationBuilder}
                quotationSettings={quotationSettings}
                quotations={quotationNumbers}
                showToast={showToast}
                confirm={confirm}
              />
            )}

            {/* ── ALL QUOTATIONS ── */}
            {tab === "All Quotations" && (
              <AllQuotationsTab
                quotations={quotations}
                customers={customers}
                designs={designs}
                filteredQuotations={filteredQuotations}
                paginatedQuotations={paginatedQuotations}
                quotationSearch={quotationSearch}
                setQuotationSearch={setQuotationSearch}
                quotationCustomerFilter={quotationCustomerFilter}
                setQuotationCustomerFilter={setQuotationCustomerFilter}
                quotationTypeFilter={quotationTypeFilter}
                setQuotationTypeFilter={setQuotationTypeFilter}
                quotationDateFrom={quotationDateFrom}
                setQuotationDateFrom={setQuotationDateFrom}
                quotationDateTo={quotationDateTo}
                setQuotationDateTo={setQuotationDateTo}
                viewQuotation={viewQuotation}
                setViewQuotation={setViewQuotation}
                setEditQuotation={setEditQuotation}
                setTab={setTab}
                handleSaveQuotation={handleSaveQuotation}
                quotationSaving={quotationSaving}
                handleDeleteQuotation={handleDeleteQuotation}
                canManageQuotations={permissions?.canManageQuotations ?? permissions?.canManageCustomers}
                handlePrintQuotation={handlePrintQuotation}
                handlePrintTransportSlip={handlePrintTransportSlip}
                handleSendWhatsApp={handleSendWhatsAppFromList}
                handleSendUpiQr={handleSendUpiQrFromList}
                formatDateTime={formatDateTime}
                formatCurrency={formatCurrency}
                quotationPage={quotationPage}
                quotationPageSize={quotationPageSize}
                quotationTotalPages={quotationTotalPages}
                setQuotationPage={setQuotationPage}
                QuotationBuilder={QuotationBuilder}
                quotationSettings={quotationSettings}
                handleOpenCustomer={handleOpenCustomerFromQuotation}
              />
            )}

            {/* ── CATEGORIES ── */}
            {tab === "Categories" && (
              <CategoriesTab
                categories={categories}
                showCatFm={showCatFm}
                setShowCatFm={setShowCatFm}
                editCat={editCat}
                setEditCat={setEditCat}
                nextCategoryOrder={nextCategoryOrder}
                setCrudBusy={setCrudBusy}
                showToast={showToast}
                handleToggleCat={handleToggleCat}
                handleDeleteCat={handleDeleteCat}
                isSuperAdmin={isSuperAdmin}
                CategoryForm={CategoryForm}
              />
            )}

            {/* ── BANNERS ── */}
            {tab === "Banners" && (
              <BannersTab
                banners={banners}
                showBanFm={showBanFm}
                setShowBanFm={setShowBanFm}
                editBan={editBan}
                setEditBan={setEditBan}
                nextBannerOrder={nextBannerOrder}
                setCrudBusy={setCrudBusy}
                showToast={showToast}
                handleDeleteBanner={handleDeleteBanner}
                BannerForm={BannerForm}
              />
            )}

            {/* ── BRANDING ── */}
            {tab === "Branding" && (
              <BrandingTab
                brandForm={brandForm}
                setBrandForm={setBrandForm}
                logoPreview={logoPreview}
                setLogoFile={setLogoFile}
                setLogoPreview={setLogoPreview}
                handleBrandSave={handleBrandSave}
                brandSaving={brandSaving}
              />
            )}

            {/* ── TEAM ── */}
            {tab === "Team" && <AdminManagement onSavingChange={setCrudBusy} />}

            {/* ── SETTINGS ── */}
            {tab === "Settings" && (
              <SettingsTab
                quotationSettings={quotationSettings}
                onSave={handleSaveQuotationSettings}
                saving={settingsSaving}
              />
            )}

            {/* ── ACTIVITY ── */}
            {tab === "Activity" && <ActivityLog />}

            {/* ── NOTIFICATIONS ── */}
            {tab === "Notifications" && (
              <NotificationsTab
                showToast={showToast}
                notificationTemplates={notificationTemplates}
                categories={categories}
                designs={activeDesigns}
                branding={branding}
                onAddTemplate={addNotificationTemplate}
                onUpdateTemplate={updateNotificationTemplate}
                onDeleteTemplate={deleteNotificationTemplate}
              />
            )}

            {/* ── APPOINTMENTS ── */}
            {tab === "Appointments" && <AppointmentsSettingsTab />}

            {/* ── BOOKINGS ── */}
            {tab === "Bookings" && <BookingsTab />}
          </section>
        </div>

      </div>

      {isCrudBusy && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(255, 255, 255, 0.82)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(1px)",
        }}>
          <Spinner size={120} />
        </div>
      )}

      {whatsappSending && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 12000,
          background: "#fff",
          border: "1px solid #25D366",
          borderRadius: 12,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
          fontFamily: fonts.body,
          fontSize: 13,
          color: B.text,
        }}>
          <span style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid #25D366", borderTopColor: "transparent", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
          <span>Sending WhatsApp message…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {quotationPrintPrompt && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="quotation-print-prompt-title"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20, 16, 12, 0.45)",
            zIndex: 11000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 460,
              background: B.white,
              border: `1px solid ${B.borderLight}`,
              borderRadius: 16,
              boxShadow: "0 24px 48px rgba(0, 0, 0, 0.18)",
              padding: "22px 22px 18px",
            }}
          >
            <h3
              id="quotation-print-prompt-title"
              style={{ margin: 0, fontSize: 20, color: B.text, fontFamily: fonts.adminDisplay }}
            >
              Quotation saved successfully
            </h3>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: B.textMid, lineHeight: 1.6, fontFamily: fonts.body }}>
              Do you want to print quotation <strong>#{quotationPrintPrompt.quotation_number || "—"}</strong> now?
            </p>
            <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Btn variant="ghost" onClick={handlePrintPromptClose}>Not now</Btn>
              <Btn
                variant="primary"
                onClick={() => {
                  handlePrintQuotation(quotationPrintPrompt);
                  handlePrintPromptClose();
                }}
              >
                Print now
              </Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
